/**
 * Google Identity Services (GIS) wrapper — replaces signInWithPopup with a
 * FedCM-based flow so console no longer shows COOP `window.close` warnings
 * (#496, follow-up of #486).
 *
 * Flow:
 * 1. Lazy-load https://accounts.google.com/gsi/client
 * 2. google.accounts.id renders Google's button inline (no popup window)
 * 3. On click, FedCM returns a JWT ID token via the registered callback
 * 4. We hand that token to Firebase via signInWithCredential — same
 *    UserCredential shape as the old signInWithPopup path
 *
 * Emulator fallback: when the Firebase Auth Emulator is connected (DEV +
 * VITE_FIRESTORE_EMULATOR_HOST set) GIS cannot validate against real Google
 * accounts, so we fall back to signInWithPopup. Same fallback applies when
 * VITE_GOOGLE_OAUTH_CLIENT_ID is missing — useful as a kill-switch to revert
 * without a code change if GIS misbehaves in production.
 */
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  type UserCredential,
} from 'firebase/auth'
import { auth } from './firebase'

const CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || ''
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

interface GisCredentialResponse {
  credential: string
  select_by?: string
}

interface GisIdApi {
  initialize: (config: {
    client_id: string
    callback: (response: GisCredentialResponse) => void
    auto_select?: boolean
    use_fedcm_for_prompt?: boolean
    // FedCM for the rendered Sign In With Google button. Without this flag
    // GIS opens an OAuth popup window on button click, defeating the whole
    // point of #496. Available Chrome 117+ / Edge 117+; in older browsers
    // GIS silently falls back to popup.
    use_fedcm_for_button?: boolean
    cancel_on_tap_outside?: boolean
    ux_mode?: 'popup' | 'redirect'
  }) => void
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon'
      theme?: 'outline' | 'filled_blue' | 'filled_black'
      size?: 'large' | 'medium' | 'small'
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
      shape?: 'rectangular' | 'pill' | 'circle' | 'square'
      logo_alignment?: 'left' | 'center'
      width?: number | string
      locale?: string
    },
  ) => void
  prompt: (notificationCb?: (notification: unknown) => void) => void
  cancel: () => void
  disableAutoSelect: () => void
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GisIdApi
      }
    }
  }
}

const isEmulatorMode = (): boolean =>
  import.meta.env.DEV && !!import.meta.env.VITE_FIRESTORE_EMULATOR_HOST

/**
 * True when GIS should be used. False forces the popup fallback (emulator,
 * missing client id, or non-browser context).
 */
export const isGisEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  if (isEmulatorMode()) return false
  return !!CLIENT_ID
}

let sdkPromise: Promise<void> | null = null

/** Idempotently inject the GIS SDK script tag and resolve when ready. */
export const loadGisSdk = (): Promise<void> => {
  if (sdkPromise) return sdkPromise
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('GIS SDK requires a browser context'))
  }
  if (window.google?.accounts?.id) return Promise.resolve()

  // On failure, clear the cached promise so a transient network blip doesn't
  // permanently poison the SDK loader for the rest of the session.
  const promise = new Promise<void>((resolve, reject) => {
    const fail = (err: Error) => {
      if (sdkPromise === promise) sdkPromise = null
      reject(err)
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    )
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        fail(new Error('Failed to load Google Identity Services SDK')),
      )
      return
    }
    const script = document.createElement('script')
    script.src = GIS_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () =>
      fail(new Error('Failed to load Google Identity Services SDK'))
    document.head.appendChild(script)
  })
  sdkPromise = promise
  return sdkPromise
}

/** Exchange a GIS-issued ID token for a Firebase UserCredential. */
export const signInWithGoogleCredential = async (
  idToken: string,
): Promise<UserCredential> => {
  const credential = GoogleAuthProvider.credential(idToken)
  return signInWithCredential(auth, credential)
}

/**
 * Popup fallback for emulator / missing-client-id environments. Mirrors the
 * pre-#496 behaviour exactly so existing flows keep working.
 */
export const signInWithGooglePopup = async (): Promise<UserCredential> => {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  return signInWithPopup(auth, provider)
}

export interface RenderGoogleButtonOptions {
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  width?: number
  locale?: string
}

/**
 * Render a Google Sign-In button into the given element. The success
 * callback receives the raw GIS JWT (Google-issued ID token); callers
 * decide how to exchange it — most use signInWithGoogleCredential, but the
 * store-owner flow forwards the token through storeLoginWithGoogle so the
 * Firebase exchange + backend init happen atomically.
 *
 * Caller must guard with isGisEnabled() and use signInWithGooglePopup() in
 * the negative branch — this function assumes GIS is available. Returns a
 * teardown function for React cleanup.
 */
export const renderGoogleButton = (
  element: HTMLElement,
  options: RenderGoogleButtonOptions,
  onSuccess: (gisIdToken: string) => void,
  onError: (err: unknown) => void,
): (() => void) => {
  let cancelled = false

  loadGisSdk()
    .then(() => {
      if (cancelled) return
      const gis = window.google?.accounts.id
      if (!gis) {
        onError(new Error('GIS SDK loaded but window.google.accounts.id missing'))
        return
      }
      gis.initialize({
        client_id: CLIENT_ID,
        callback: (response) => {
          if (cancelled) return
          try {
            onSuccess(response.credential)
          } catch (err) {
            onError(err)
          }
        },
        auto_select: false,
        use_fedcm_for_prompt: true,
        use_fedcm_for_button: true,
        cancel_on_tap_outside: true,
      })
      gis.renderButton(element, {
        type: 'standard',
        theme: options.theme ?? 'outline',
        size: options.size ?? 'large',
        text: options.text ?? 'signin_with',
        shape: options.shape ?? 'rectangular',
        logo_alignment: 'center',
        width: options.width,
        locale: options.locale,
      })
    })
    .catch(onError)

  return () => {
    cancelled = true
    // Clear the button so a re-render (locale change, etc.) doesn't stack a
    // second button next to the first one. GIS doesn't expose an explicit
    // unmount API, but the DOM under our control is ours to wipe.
    element.replaceChildren()
  }
}
